<script setup lang="ts">
/**
 * Choix d'une date sur mesure : champ qui ouvre un calendrier dans un sheet. Semaine du lundi,
 * libellés français, raccourcis « Aujourd'hui » et « Hier ». Valeur au format AAAA-MM-JJ.
 * Jamais d'`<input type="date">` natif.
 */
import { getLocalTimeZone, parseDate, today, type DateValue } from '@internationalized/date'
import {
  CalendarCell,
  CalendarCellTrigger,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHead,
  CalendarGridRow,
  CalendarHeadCell,
  CalendarHeading,
  CalendarNext,
  CalendarPrev,
  CalendarRoot,
} from 'reka-ui'
import { computed, ref, useId } from 'vue'

import { fr } from '@/copy/fr'

import Field from './Field.vue'
import Icon from './Icon.vue'
import Sheet from './Sheet.vue'

const props = withDefaults(
  defineProps<{
    label: string
    hint?: string
    error?: string
    /** Date maximale (AAAA-MM-JJ) : par défaut aucune. */
    max?: string
    timeZone?: string
  }>(),
  { hint: undefined, error: undefined, max: undefined, timeZone: undefined },
)

const model = defineModel<string>({ required: true })
const id = useId()
const open = ref(false)

const zone = computed(() => props.timeZone ?? getLocalTimeZone())
const todayDate = computed(() => today(zone.value))
const value = computed(() => parseDate(model.value))
const maxValue = computed(() => (props.max ? parseDate(props.max) : undefined))

const LONG_DATE = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
})

const displayed = computed(() => {
  const date = value.value
  if (date.compare(todayDate.value) === 0) return fr.ui.today
  if (date.compare(todayDate.value.subtract({ days: 1 })) === 0) return fr.ui.yesterday
  return LONG_DATE.format(date.toDate('UTC'))
})

function pick(date: DateValue | undefined) {
  if (!date) return
  model.value = date.toString().slice(0, 10)
  open.value = false
}

const shortcuts = computed(() => [
  { label: fr.ui.today, date: todayDate.value },
  { label: fr.ui.yesterday, date: todayDate.value.subtract({ days: 1 }) },
])
</script>

<template>
  <Field :id="id" :label="props.label" :hint="hint" :error="error">
    <template #default="{ describedBy }">
      <button
        :id="id"
        type="button"
        aria-haspopup="dialog"
        :aria-expanded="open"
        :aria-describedby="describedBy"
        class="flex h-control w-full items-center gap-3 rounded-control bg-surface px-4 text-left transition-colors duration-80 active:bg-surface-strong"
        @click="open = true"
      >
        <Icon name="calendar" class="text-ink-muted" />
        <span class="flex-1 truncate text-body text-ink first-letter:uppercase">{{
          displayed
        }}</span>
        <Icon name="chevron-down" class="text-ink-muted" />
      </button>
    </template>
  </Field>

  <Sheet v-model:open="open" :title="props.label">
    <div class="mb-4 flex gap-2">
      <button
        v-for="shortcut in shortcuts"
        :key="shortcut.label"
        type="button"
        :class="[
          'h-control-compact rounded-full px-4 text-label transition-colors duration-80',
          shortcut.date.compare(value) === 0
            ? 'bg-accent-soft text-accent-strong'
            : 'bg-surface text-ink active:bg-surface-strong',
        ]"
        @click="pick(shortcut.date)"
      >
        {{ shortcut.label }}
      </button>
    </div>

    <CalendarRoot
      v-slot="{ weekDays, grid }"
      :model-value="value"
      :max-value="maxValue"
      locale="fr-FR"
      :week-starts-on="1"
      weekday-format="short"
      fixed-weeks
      class="flex flex-col gap-3"
      @update:model-value="pick"
    >
      <div class="flex items-center justify-between">
        <CalendarPrev
          class="inline-flex size-touch items-center justify-center rounded-full text-ink active:bg-surface"
          :aria-label="fr.ui.previousMonth"
        >
          <Icon name="back" :size="24" />
        </CalendarPrev>
        <CalendarHeading class="text-section text-ink first-letter:uppercase" />
        <CalendarNext
          class="inline-flex size-touch items-center justify-center rounded-full text-ink active:bg-surface data-[disabled]:text-ink-disabled"
          :aria-label="fr.ui.nextMonth"
        >
          <Icon name="forward" :size="24" />
        </CalendarNext>
      </div>

      <CalendarGrid
        v-for="month in grid"
        :key="month.value.toString()"
        class="w-full border-collapse"
      >
        <CalendarGridHead>
          <CalendarGridRow class="grid grid-cols-7">
            <CalendarHeadCell
              v-for="day in weekDays"
              :key="day"
              class="py-1 text-center text-caption text-ink-muted"
            >
              {{ day.replace('.', '') }}
            </CalendarHeadCell>
          </CalendarGridRow>
        </CalendarGridHead>
        <CalendarGridBody>
          <CalendarGridRow
            v-for="(week, index) in month.rows"
            :key="index"
            class="grid grid-cols-7"
          >
            <CalendarCell
              v-for="date in week"
              :key="date.toString()"
              :date="date"
              class="flex justify-center py-0.5"
            >
              <CalendarCellTrigger
                :day="date"
                :month="month.value"
                class="flex size-11 items-center justify-center rounded-full text-body text-ink tabular-nums transition-colors duration-80 outline-none focus-visible:outline-2 focus-visible:outline-accent active:bg-surface data-[disabled]:text-ink-disabled data-[outside-view]:text-ink-disabled data-[selected]:bg-accent-strong data-[selected]:text-ink-inverse data-[today]:font-semibold data-[today]:text-accent-strong data-[today]:data-[selected]:text-ink-inverse"
              />
            </CalendarCell>
          </CalendarGridRow>
        </CalendarGridBody>
      </CalendarGrid>
    </CalendarRoot>
  </Sheet>
</template>
